import React, { useEffect, useState } from 'react';

import { Button, Flex, Menu, ScrollArea, Table, useMantineTheme } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { IconCsv, IconDownload, IconJson, IconUsers } from '@tabler/icons-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { NoDataFound } from 'components/NoDataFound';
import { UpgradePlanBlurCard } from 'components/UpgradePlanBlurCard';
import { useSubscriptionContext } from 'contexts/SubscriptionContext';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { useTranslation } from 'react-i18next';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { LanguageService } from 'services/language-service';
import { LeadsService } from 'services/leads-service';
import { NotificationService } from 'services/notification-service';
import { SMART_ASSISTANT_LEADS_QUERY_KEY } from 'shared/constants/query-keys';
import {
  ExportSmartAssistantLeadsDto,
  GetSmartAssistantLeadsQueryDto,
  LEADS_EXPORT_TYPE_TO_FILE_EXTENSION,
  LEADS_EXPORT_TYPE_TO_MIME_TYPE,
  LeadsExportType
} from 'shared/interfaces/Lead';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';
import { ToolKind } from 'shared/interfaces/tools/Tool';
import {
  DAY_IN_MILLISECONDS,
  DEFAULT_DATE_RANGE,
  DateRangePickerValue,
  getReadableFormatDateTime
} from 'shared/utils/date';

interface LeadsAnalyticsSectionProps {
  smartAssistant: SmartAssistant;
  isGuest?: boolean;
}

export function LeadsAnalyticsSection({ smartAssistant, isGuest = false }: LeadsAnalyticsSectionProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const [subscription] = useSubscriptionContext();
  const [dateRange, setDateRange] = useState<DateRangePickerValue>(DEFAULT_DATE_RANGE);
  const enabledTools = subscription.product.enabledTools ?? [];

  const getLeadsQuery = (): GetSmartAssistantLeadsQueryDto => {
    if (!dateRange) return {};
    else if (!dateRange[0] || !dateRange[1]) return {};
    else return { start: dateRange[0].getTime(), end: dateRange[1].getTime() + DAY_IN_MILLISECONDS - 1 };
  };

  const smartAssistantLeadsQuery = useQuery({
    queryKey: [SMART_ASSISTANT_LEADS_QUERY_KEY, { smartAssistantId: smartAssistant._id }],
    queryFn: () =>
      (isGuest ? GuestSmartAssistantService : LeadsService).getSmartAssistantLeads({
        smartAssistantId: smartAssistant._id,
        getSmartAssistantLeadsQueryDto: getLeadsQuery()
      }),
    enabled: enabledTools.includes(ToolKind.COLLECT_LEAD_INFO) || isGuest
  });

  const exportLeadsMutation = useMutation({
    mutationFn: (isGuest ? GuestSmartAssistantService : LeadsService).exportSmartAssistantLeads,
    onSuccess: (buffer: ArrayBuffer, arg: ExportSmartAssistantLeadsDto) => {
      const mimeType = LEADS_EXPORT_TYPE_TO_MIME_TYPE.get(arg.type);
      const blob = new Blob([buffer], { type: mimeType });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute(
        'download',
        t('SMART_ASSISTANT.LEADS_SECTION.EXPORT.FILE_NAME', {
          fileExtension: LEADS_EXPORT_TYPE_TO_FILE_EXTENSION.get(arg.type)
        })
      );
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.LEADS.FAILURE.EXPORT')
  });

  const exportLeads = (type: LeadsExportType) => {
    exportLeadsMutation.mutate({
      smartAssistantId: smartAssistant._id,
      query: getLeadsQuery(),
      type
    });
  };

  useEffect(() => {
    if (!dateRange || !(enabledTools.includes(ToolKind.COLLECT_LEAD_INFO) || isGuest)) return;
    if ((!dateRange[0] && !dateRange[1]) || (dateRange[0] && dateRange[1])) smartAssistantLeadsQuery.refetch();
  }, [dateRange]);

  if (smartAssistantLeadsQuery.isLoading && (enabledTools.includes(ToolKind.COLLECT_LEAD_INFO) || isGuest))
    return <FullScreenLoader />;
  if (smartAssistantLeadsQuery.isError) return <ErrorAlert />;

  const leads = smartAssistantLeadsQuery.data || [];
  const hasLeads = leads.length > 0;

  const leadRows = leads
    // Sort leads by createdAt in descending order (newest first)
    .sort((leadA, leadB) => leadB.createdAt - leadA.createdAt)
    .map((lead, index) => (
      <tr key={index}>
        <td>{lead.email || ''}</td>
        <td>{lead.name || ''}</td>
        <td>{lead.phone || ''}</td>
        <td>{getReadableFormatDateTime(lead.createdAt)}</td>
      </tr>
    ));

  return (
    <ManageSmartAssistantSectionControlWrapper title={t('SMART_ASSISTANT.LEADS_SECTION.TITLE')}>
      <UpgradePlanBlurCard
        message={t('SMART_ASSISTANT.LEADS_SECTION.UPGRADE.DESCRIPTION')}
        showCard={!(enabledTools.includes(ToolKind.COLLECT_LEAD_INFO) || isGuest)}>
        {hasLeads && (
          <Flex direction="row" align="center" justify="space-between">
            <DatePickerInput
              w={330}
              type="range"
              locale={LanguageService.getCurrentLanguage()}
              label={t('SMART_ASSISTANT.LEADS_SECTION.SELECT_DATE_RANGE')}
              value={dateRange}
              onChange={setDateRange}
            />
            <Menu>
              <Menu.Target>
                <Button
                  rightIcon={<IconDownload size={20} />}
                  disabled={!hasLeads}
                  loading={exportLeadsMutation.isLoading}>
                  {t('SMART_ASSISTANT.LEADS_SECTION.EXPORT.TITLE')}
                </Button>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Label>{t('SMART_ASSISTANT.LEADS_SECTION.EXPORT.LABEL')}</Menu.Label>
                <Menu.Item icon={<IconJson size={14} />} onClick={() => exportLeads(LeadsExportType.JSON)}>
                  {t('SMART_ASSISTANT.LEADS_SECTION.EXPORT.JSON')}
                </Menu.Item>
                <Menu.Item icon={<IconCsv size={14} />} onClick={() => exportLeads(LeadsExportType.CSV)}>
                  {t('SMART_ASSISTANT.LEADS_SECTION.EXPORT.CSV')}
                </Menu.Item>
                {/* Add more export types here */}
              </Menu.Dropdown>
            </Menu>
          </Flex>
        )}
        <Flex direction="column" h="100%" style={{ overflowY: 'auto' }}>
          {!hasLeads ? (
            <NoDataFound
              h="100%"
              w="100%"
              icon={<IconUsers />}
              title={t('NO_DATA_FOUND.LEADS.TITLE')}
              subTitle={t('NO_DATA_FOUND.LEADS.SUB_TITLE')}
            />
          ) : (
            <ScrollArea h="100%" mt={15} style={{ border: `1px solid ${theme.colors.gray[3]}`, borderRadius: '5px' }}>
              <Table>
                <thead>
                  <tr
                    style={{
                      position: 'sticky',
                      top: -1,
                      backgroundColor: theme.colors.gray[0]
                    }}>
                    <th>{t('SMART_ASSISTANT.LEADS_SECTION.TABLE.EMAIL_COLUMN_TITLE')}</th>
                    <th>{t('SMART_ASSISTANT.LEADS_SECTION.TABLE.NAME_COLUMN_TITLE')}</th>
                    <th>{t('SMART_ASSISTANT.LEADS_SECTION.TABLE.PHONE_COLUMN_TITLE')}</th>
                    <th>{t('SMART_ASSISTANT.LEADS_SECTION.TABLE.CREATED_AT_COLUMN_TITLE')}</th>
                  </tr>
                </thead>
                <tbody>{leadRows}</tbody>
              </Table>
            </ScrollArea>
          )}
        </Flex>
      </UpgradePlanBlurCard>
    </ManageSmartAssistantSectionControlWrapper>
  );
}
